<script lang="ts" setup>
  import { useLocale } from '@tundr/i18n';
  import { Dropdown, Filter, FilterVariant } from '@tundr/ds-components';
  import { computed, onUnmounted } from 'vue';
  import type {
    Filter as FilterModel,
    MultipleSelectModelValue,
  } from '../../../store/';
  import {
    FilterTypes,
    getSelectOptionKey,
    parseModelFromFilterType,
  } from '../../../utils';
  import { FilterByType } from '../filter-components';
  import { activeFilterContainerClass } from './ActiveFilterChip.css';

  const props = withDefaults(
    defineProps<{
      filterNamespace: string;
      filter: FilterModel;
      persistent?: boolean;
      disabled?: boolean;
    }>(),
    {
      persistent: false,
      disabled: false,
    },
  );

  const { d } = useLocale('');

  const datePickerMenuSelectors = ['.dp--menu', '.dp--menu-wrapper'];

  const emit = defineEmits<{
    removeFilter: [filter: FilterModel];
  }>();

  const chipCount = computed(() => {
    const { filterType, modelValue } = props.filter;
    if (filterType === FilterTypes.SELECT_MULTIPLE && modelValue) {
      return (modelValue as MultipleSelectModelValue).length;
    }
    return undefined;
  });

  const chipCountTooltip = computed(() => {
    const { filterType, modelValue } = props.filter;
    if (filterType === FilterTypes.SELECT_MULTIPLE && modelValue) {
      return (modelValue as MultipleSelectModelValue)
        .map((item) => item.label ?? getSelectOptionKey(item.value))
        .join('\n');
    }
    return undefined;
  });

  const chipValue = computed(() => {
    const { filterType, modelValue } = props.filter;
    if (!modelValue || filterType === FilterTypes.SELECT_MULTIPLE) {
      return undefined;
    }
    return parseModelFromFilterType(filterType, modelValue, d);
  });

  /*
          A disabled chip must not be force-opened, and must not report a close either:
          onDropdownOpenChange would discard a still-empty non-persistent filter.
        */
  const dropdownOpen = computed(() => {
    if (props.disabled) return undefined;
    return props.persistent ? undefined : !props.filter.modelValue;
  });

  const onRemoveFilter = () => {
    emit('removeFilter', props.filter);
  };

  let discarded = false;

  const discardIfEmpty = () => {
    if (discarded || props.persistent || props.filter.modelValue) return;
    discarded = true;
    onRemoveFilter();
  };

  const onDropdownOpenChange = (open: boolean) => {
    if (open) return;
    discardIfEmpty();
  };

  onUnmounted(discardIfEmpty);
</script>

<template>
  <Dropdown
    :open="dropdownOpen"
    :disabled="disabled"
    :ignored-outside-selectors="datePickerMenuSelectors"
    :fixed-height="true"
    :with-padding="false"
    trigger-as-child
    @update:open="onDropdownOpenChange"
  >
    <template #trigger>
      <Filter
        :variant="FilterVariant.Table"
        :label="filter.filterLabel"
        :value="chipValue"
        :count="chipCount"
        :count-tooltip="chipCountTooltip"
        :inactive="!filter.modelValue"
        :disabled="disabled"
        @remove="onRemoveFilter"
      />
    </template>
    <div :class="activeFilterContainerClass">
      <FilterByType
        :filter-namespace="filterNamespace"
        v-bind="{ ...$attrs, ...filter }"
        :items="'items' in filter ? filter.items : []"
        :can-load-more="
          'canLoadMore' in filter ? filter.canLoadMore : () => false
        "
        :on-load-more="'onLoadMore' in filter ? filter.onLoadMore : () => {}"
        :on-search="'onSearch' in filter ? filter.onSearch : () => {}"
      >
        <template #additional-filters>
          <slot name="additional-filters"></slot>
        </template>
      </FilterByType>
    </div>
  </Dropdown>
</template>
